import { Link } from "react-router-dom";
import { Avatar, Box, Skeleton, Typography } from "@mui/material";

import { User } from "../../../types/types";

import { useUserDataFormatter } from "../../User/hooks/useUserDataFormatter";

import { UserVerifiedIcon } from "../assets/icons/gino-icons";

export const UserCard = ({ user, flexible }: { user: User; flexible?: boolean }) => {
  const {
    fullName,
    firstTwoChr,
    club,
    media,
    role,
    isVerified,
    profileLink,
    playerPositionShort,
    additionalProperties,
  } = useUserDataFormatter(user);

  return (
    <Box sx={styles.userCard(flexible)}>
      <Box sx={styles.userAvatarContainer}>
        <Avatar sx={{ width: "56px", height: "56px" }} src={media}>
          {firstTwoChr}
        </Avatar>
        {isVerified && <UserVerifiedIcon sx={styles.userVerifiedIcon} />}
      </Box>
      <Box sx={styles.userInfo}>
        <Typography component={Link} to={profileLink} sx={styles.userName}>
          {fullName}
        </Typography>
        <Typography sx={styles.userInfoText}>{`${role} ${
          !!playerPositionShort ? ` • (${playerPositionShort})` : ""
        }`}</Typography>
        <Typography sx={styles.userInfoText}>{club}</Typography>
      </Box>
      <Box sx={styles.userInfoDataContainer}>
        {additionalProperties.map(
          ({ label, value }) =>
            value && (
              <Box sx={styles.userInfoDataItem}>
                <Typography component="label" sx={styles.userInfoLabel} title={label}>
                  {label}
                </Typography>
                <Typography component="data" sx={styles.userInfoData}>
                  {value}
                </Typography>
              </Box>
            )
        )}
      </Box>
    </Box>
  );
};

export const UserLoadingCard = ({ flexible }: { flexible?: boolean }) => {
  return (
    <Box sx={styles.userCard(flexible)}>
      <Skeleton variant="circular" width={56} height={56} />
      <Box sx={styles.userInfo}>
        <Skeleton variant="text" width={100} height={20} />
        <Skeleton variant="text" width={60} height={20} />
      </Box>
      <Box sx={styles.userInfoDataContainer}>
        {Array.from({ length: 3 }).map((_, idx) => (
          <Box sx={styles.userInfoDataItem}>
            <Skeleton variant="text" width={idx % 2 === 0 ? 100 : 120} height={20} />
            <Skeleton variant="text" width={idx % 2 === 0 ? 60 : 40} height={20} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const styles = {
  userCard: (flexible?: boolean) => ({
    flex: flexible ? "0 0 calc(33.33% - 11px)" : "unset",
    flexShrink: 0,
    width: flexible ? "calc(33.33% - 11px)" : "220px",
    minHeight: "274.133px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #E7E7E7",
    background: "#FFF",
    boxShadow: "0 12px 20px 0 rgba(0, 0, 0, 0.04), 0 0 2px 0 rgba(0, 0, 0, 0.06)",
  }),
  userAvatarContainer: {
    position: "relative",
  },
  userAvatar: {
    width: "56px",
    height: "56px",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: "4px",
    textAlign: "center",
  },
  userName: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "18px",
    textDecoration: "none",
  },
  userInfoText: {
    fontFamily: "Montserrat",
    color: "var(--Dark---600, #707070)",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "18px",
  },
  userInfoLabel: {
    color: "var(--Gray---400, #A0A0A0)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
    maxWidth: "75%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  userInfoDataItem: {
    display: "flex",
    justifyContent: "space-between",
  },
  userInfoData: {
    color: "var(--Dark---1000, #111)",
    fontFamily: "Inter",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  userVerifiedIcon: {
    position: "absolute",
    bottom: "-5px",
    left: "50%  ",
    transform: "translateX(-50%)",
  },
  userInfoDataContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    flexShrink: 0,
    flex: 1,
  },
};
